import { GENERAL_ID } from '../../components/Stores/exposureDataStore';
import { IFutureOrder } from '../../sp-types/FutureOrderType';
import { IInventoryReportType } from '../../sp-types/InventoryReportType';
import { DEFAULT_COLOR, GENERAL } from '../../sp-types/InventoryType';
import { Account, IMaterialToAccount, IRiskPercent, IRiskTolerance } from '../../sp-types/accountTypes';
import { StackedBarChartElement } from '../../sp-types/dashboardTypes';
import { IFinancialPosition, IPositionMaterial } from '../../sp-types/financialPositionType';
import { Material } from '../../sp-types/materialType';
import { IMaterialExposure, INetExposure } from '../../sp-types/netExposureType';
import { monthsFullWord, MonthYear } from '../../utils/MonthYear/monthYear';
import { ExposureTypes, getExposureStatus } from '../../utils/RiskUtils';
import { getExposureCalc, getGeneralExposure, getNetExposure, isSameMonth } from '../../utils/exposureUtils';
import { fixMonthYear } from '../../utils/inventoryUtils';
import { addColors, getDate, getDateStr, monthDiff } from '../FinancialPositions/components/FinancialPositionsUtils';
import { aggregateByMaterial, positionsByMaterial } from '../FinancialPositions/components/PositionsDataUtils';
import { IExposureCardProps } from './ExposureCard';
import { singleMaterialCardProps } from './MaterialTrendButton';
import { months } from '../../utils/MonthYear/monthYear';
import { getMonthYearAfter } from '../FutureOrder/FutureOrders/Utils';
import { MonthYearValue } from '../../utils/MonthYear/MonthYearValue';
/* eslint-disable */

export const MULTIPLE_MATS = 'Multiple materials';
export const NO_DATA = 'No data';

export const terminalChips = [
  { key: 'Months', value: 0 },
  { key: 'Terminals', value: 1 },
];

export const valChips = [
  { key: 'Value', value: 0 },
  { key: 'Quantity', value: 1 },
];

export const priceChips = [
  { key: 'Market Price', value: 0 },
  { key: 'Actual price', value: 1 },
];

export const cumulaticeChips = [
  { key: 'Cumulative', value: 1 },
  { key: 'Incremental', value: 0 },
];

export type MatReportSummary = {
  lastPrice: number;
  quantity: number;
  monthVal: Record<string, { price: number; ratio?: number; quantity?: number }>;
  month: number;
  unit?: string;
  ratio: number;
};
export type DashboardSummaryMap = Record<string, MatReportSummary>;

export type DashboardMap = Record<
  string,
  {
    sumValue: number;
    valuesArray: { date: MonthYear; value: number }[];
  }
>;

export const reportsToSummaryMap = (
  inventoryReports: IInventoryReportType[],
  selectedYear: number,
  materialsMap: Record<string, Material>,
  pricesMap: Record<string, number>,
  isActualPrice: boolean,
  getCurrencyInUSD: (_currency: string) => number = () => 1,
): DashboardSummaryMap => {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  // Storage month is previous month
  const storageDate = new Date(currentYear, currentMonth - 1, 1);
  const storageMonthKey = `${storageDate.getFullYear()}-${storageDate.getMonth() + 1}`;

  return inventoryReports
    .map((material) => fixMonthYear(material))
    .filter(({ ratio, dontSum }) => (!ratio || ratio === 0) && !dontSum) //remove ratio entries and dontSum records
    .sort((a: IInventoryReportType, b: IInventoryReportType) => a.reportingTime.seconds - b.reportingTime.seconds)
    .reduce((acc, { materialId, quantity, monthYear, ratio, actualPrice, suppliedQty, reportingTime, currency }) => {
      acc[materialId] = acc[materialId] || { quantity: 0, lastPrice: 0, monthVal: {}, month: 0, ratio: 0 };

      // Calculate remaining quantity: quantity - suppliedQty (min 0)
      const remainingQuantity = Math.max(0, (quantity || 0) - (suppliedQty || 0));

      // When using actual price, convert to USD using currency rate
      const price =
        isActualPrice && actualPrice ? actualPrice * getCurrencyInUSD(currency || '') : pricesMap[materialId];

      const materialPrice = +(remainingQuantity * price);

      // Always include ALL inventory in the totals for the donut chart (lastPrice and quantity)
      acc[materialId] = {
        lastPrice: acc[materialId].lastPrice + materialPrice,
        month: monthYear.month,
        quantity: acc[materialId].quantity + remainingQuantity,
        monthVal: acc[materialId].monthVal,
        unit: materialsMap[materialId]?.unit,
        ratio: acc[materialId].ratio + (ratio || 0),
      };

      // For bar chart: aggregate all inventory from storage month (previous month) and older into storage bar
      const reportDate = reportingTime.toDate();
      const currentMonthStart = new Date(currentYear, currentMonth, 1);
      const isStorageOrOlder = reportDate < currentMonthStart;

      const monthKey = isStorageOrOlder ? storageMonthKey : `${monthYear.year}-${monthYear.month}`;

      acc[materialId].monthVal[monthKey] = {
        price: (acc[materialId].monthVal[monthKey]?.price || 0) + materialPrice,
        ratio,
        quantity: (acc[materialId].monthVal[monthKey]?.quantity || 0) + remainingQuantity,
      };

      return acc;
    }, {} as DashboardSummaryMap);
};

export const getEntriesFiltered = (
  allMaterials: DashboardSummaryMap,
  currMaterialId?: string,
): [string, MatReportSummary][] => {
  if (currMaterialId) {
    const remainSum = Object.entries(allMaterials)
      .filter(([materialId, _]) => materialId !== currMaterialId)
      .reduce((acc, [_, { lastPrice: sumPrice }]) => acc + sumPrice, 0);
    allMaterials['placeholder'] = { lastPrice: remainSum, quantity: 0, monthVal: {}, month: 0, ratio: 0 };
  }

  return Object.entries(allMaterials).filter(
    ([materialId]) => !currMaterialId || [currMaterialId, 'placeholder'].includes(materialId),
  );
};

export const invReportsToStackedBar = (
  materialsEntries: [string, MatReportSummary][],
  materialsMap: Record<string, Material>,
  colorsMap: Record<string, string>,
  isQuantity: boolean = false,
): { materialId: string; fieldName: string; color: string; valueArray: MonthYearValue[] }[] =>
  materialsEntries.map(([materialId, { monthVal }]) => ({
    materialId,
    fieldName: materialsMap[materialId]?.name,
    color: colorsMap[materialId] || DEFAULT_COLOR,
    valueArray: ['_storag', ...months]
      .map((_, monthIndex) => getMonthYearAfter(new Date(), monthIndex - 1))
      .map(({ month, year }) => ({
        value: (isQuantity ? monthVal[`${year}-${month}`]?.quantity : monthVal[`${year}-${month}`]?.price) || 0,
        date: { month, year },
      })),
  }));

export const mapPositions = (
  positions: IFinancialPosition[],
  materialId: string,
  materialsMap: Record<string, Material>,
  colorsMap: Record<string, string>,
  pricesMap: Record<string, number>,
  isAccumulate: boolean,
): StackedBarChartElement[] => {
  const aggPositions = aggregateByMaterial(
    positionsByMaterial(addColors(positions), false),
    materialsMap,
    colorsMap,
    pricesMap,
  );

  const dashboardMap = calulateMonthSpread(aggPositions, isAccumulate);

  return materialMapToFiltered(
    dashboardMap,
    colorsMap,
    materialsMap,
    pricesMap,
    materialId === GENERAL_ID ? undefined : materialId,
  );
};

type dateValue = { date: string; value: number };
const makeMap = (values: dateValue[]): Record<string, number> =>
  values.reduce((acc, { date, value }) => ({ ...acc, [date]: value }), {});

const joinOverlapDates = (dateVals?: dateValue[]): dateValue[] =>
  (dateVals &&
    Object.entries(
      dateVals.reduce(
        (acc, { date, value }) => ({ ...acc, [date]: (acc[date] || 0) + value }),
        {} as Record<string, number>,
      ),
    ).map(([date, value]) => ({ date, value }))) ||
  [];

const joinMonthArrays = (existingVals: dateValue[], addedValues: dateValue[]): dateValue[] => {
  const existingMap = makeMap(existingVals);
  const addingMap = makeMap(addedValues);

  return [
    ...existingVals.map(({ date, value }) => ({ date, value: value + (addingMap[date] || 0) })),
    ...(addedValues.filter(({ date }) => !existingMap[date]) || []),
  ];
};

const makeExposureCalendar = (fromDate: Date, toDate: Date, value: number, filterFrom: Date): dateValue[] =>
  Array.from({ length: monthDiff(fromDate, toDate) })
    .map((_, i) => getDate(fromDate, i))
    .filter((date) => date > filterFrom || isSameMonth(date, filterFrom))
    .map((date) => ({ date: getDateStr(date), value }));

const getMonthYear = (date: string): MonthYear => ({ year: +date.substring(0, 4), month: +date.substring(4) });

export const calulateMonthSpread = (materials: IPositionMaterial[], isAccumulate: boolean): DashboardMap =>
  materials.reduce((result, { totalPrice, materialId, positions }) => {
    const jointArray = isAccumulate
      ? positions
          ?.map(({ totalPrice, expiryDate, createDate }) =>
            makeExposureCalendar(createDate.toDate(), expiryDate.toDate(), totalPrice, new Date()),
          )
          .reduce((res, calendar) => joinMonthArrays(res, calendar), []) || []
      : joinOverlapDates(
          positions?.map(({ totalPrice, expiryDate }) => ({
            date: getDateStr(expiryDate.toDate()),
            value: totalPrice,
          })),
        );

    const valuesArray = fillAllMonths(jointArray).map(({ date, value }) => ({ value, date: getMonthYear(date) })) || [];

    return { ...result, [materialId]: { sumValue: totalPrice, valuesArray } };
  }, {} as DashboardMap);

const getCurrMonthValue = (valuesArray: { date: MonthYear; value: number }[], now: Date): number =>
  valuesArray.find(({ date }) => date.year === now.getFullYear() && date.month === now.getMonth() + 1)?.value || 0;

export const materialMapToFiltered = (
  allMaterials: DashboardMap,
  colorsMap: Record<string, string>,
  materialsMap: Record<string, Material>,
  pricesMap: Record<string, number>,
  selectedMaterial?: string,
): StackedBarChartElement[] => {
  if (selectedMaterial) {
    const otherMaterials = Object.entries(allMaterials).filter(([materialId, _]) => materialId !== selectedMaterial);
    const remainSum = otherMaterials?.reduce((acc, [_, { sumValue }]) => acc + sumValue, 0) || 0;
    const now = new Date();

    const currMonthVal =
      otherMaterials?.reduce((acc, [_, { valuesArray }]) => acc + getCurrMonthValue(valuesArray, now), 0) || 0;

    allMaterials['placeholder'] = {
      sumValue: remainSum,
      valuesArray: [{ date: { year: now.getFullYear(), month: now.getMonth() + 1 }, value: currMonthVal }],
    };
  }

  return Object.entries(allMaterials)
    .filter(([materialId, _]) => !selectedMaterial || [selectedMaterial, 'placeholder'].includes(materialId))
    .map(
      ([materialId, { sumValue, valuesArray }]): StackedBarChartElement => ({
        materialId,
        fieldName: materialsMap[materialId]?.name,
        unit: materialsMap[materialId]?.unit,
        price: pricesMap[materialId] || 0,
        color: colorsMap[materialId] || DEFAULT_COLOR,
        valueArray: valuesArray,
        sumValue,
      }),
    );
};

const fillAllMonths = (dateValues: dateValue[]): dateValue[] => {
  const now = new Date();
  const valueMap = makeMap(dateValues);

  const dates = monthsFullWord
    .map((_, i) => getDateStr(getDate(now, i)))
    .map((date: string) => ({ date, value: valueMap[date] || 0 }));

  return dates;
};

export const getMaterialsFromRows = (
  inventoryReports: IInventoryReportType[],
  futureOrders: IFutureOrder[],
): string[] =>
  Array.from(
    new Set([
      ...inventoryReports.map((a) => a.materialId),
      ...futureOrders.reduce(
        (acc, { supplierExposure }) => [...acc, ...(supplierExposure?.map((b) => b.materialId) || [])],
        [] as string[],
      ),
    ]),
  );

export const getMaterialStatusCards = (
  materialIds: string[],
  materialsMap: Record<string, Material>,
  colorsMap: Record<string, string>,
  exposureType: string,
) => {
  const materialCards = materialIds.map((materialId, i) => ({
    materialId,
    materialName: materialsMap[materialId]?.name || GENERAL,
    exposureStatus: exposureType,
    cardColor: colorsMap[materialId] || '',
  }));
  return materialCards;
};

export const yearRange = (startingYear: number = 2000, numberOfYears: number = 8) =>
  Array.from(Array(numberOfYears)).map((_, i) => startingYear + i);

const convertToUSD = (matExposure: IMaterialExposure, convertRate: number): IMaterialExposure => ({
  ...(matExposure || {}),
  financialPositions: matExposure?.financialPositions * convertRate || 0,
  futureOrder: matExposure?.futureOrder * convertRate || 0,
  inventorySum: matExposure?.inventorySum * convertRate || 0,
});

const calcGeneralVolatility = (
  exposure: INetExposure,
  materialsMap: Record<string, Material>,
  convertionMap: Record<string, number>,
): string => {
  const matExposures = Object.entries(exposure.materialsExposure)
    .filter(([materialId]) => materialId !== GENERAL_ID)
    .map(([materialId, matExposure]) => ({
      materialId,
      exposure: Math.abs(getExposureCalc(matExposure)) * convertionMap[materialId],
      volatility: materialsMap[materialId]?.volatility || '70%',
    }));

  const totalVolatility = matExposures.reduce(
    (acc, { exposure, volatility }) =>
      acc + exposure * (volatility ? parseFloat(volatility.replace('%', '')) / 100 : 0),
    0,
  );
  const totalExposure = matExposures.reduce((acc, { exposure }) => acc + exposure, 0);

  return totalExposure > 0 ? `${((totalVolatility / totalExposure) * 100).toFixed(1)}%` : '0%';
};

export const getExposureCardData = (
  exposureData: INetExposure,
  accountMaterialData: IMaterialToAccount,
  accountRiskPerc: IRiskPercent,
  convertionMap: Record<string, number>,
  materialsMap: Record<string, Material>,
  currentMaterial?: Material | null,
): IExposureCardProps => {
  const currentExposure =
    currentMaterial && currentMaterial.id !== GENERAL
      ? convertToUSD(exposureData.materialsExposure[currentMaterial.id], convertionMap[currentMaterial.id])
      : getGeneralExposure(exposureData, convertionMap);
  const netExposure = currentExposure.inventorySum + currentExposure.financialPositions - currentExposure.futureOrder;
  const riskPercent =
    currentMaterial && currentMaterial.id !== GENERAL
      ? ({
          volatility: currentMaterial.volatility,
          riskBackTesting: currentMaterial.riskBackTesting,
        } as IRiskPercent)
      : { ...accountRiskPerc, volatility: calcGeneralVolatility(exposureData, materialsMap, convertionMap) };
  return {
    gaugeBarProps: { riskTolerance: accountMaterialData.RiskTolerance, currentValue: netExposure },
    netExposure,
    riskPercent,
    selectedMaterial: currentMaterial?.id || '',
    totalAmounts: currentExposure,
  };
};

export const getMaterialsCardData = (
  materialsMap: Record<string, Material>,
  colorsMap: Record<string, string>,
  convertionMap: Record<string, number>,
  materialsToAccount: IMaterialToAccount[],
  exposureData: INetExposure,
  addMaterials?: string[],
): singleMaterialCardProps[] => {
  return [
    ...materialsToAccount.map(({ materialId }) => ({
      materialId,
      materialName: materialsMap[materialId]?.name || GENERAL,
      exposureStatus: getExposureStatus(
        materialsToAccount.find((item) => item.materialId === materialId)?.RiskTolerance || emptyRiskTolerance,
        getNetExposure(exposureData, convertionMap, materialId, materialId === GENERAL_ID),
      ),
      cardColor: colorsMap[materialId] || '',
    })),
    ...(addMaterials || []).map((materialId) => ({
      materialId,
      materialName: materialsMap[materialId]?.name || GENERAL,
      exposureStatus: ExposureTypes.stable,
      cardColor: colorsMap[materialId] || '',
    })),
  ];
};

const emptyRiskTolerance: IRiskTolerance = {
  dangerAreaMinus: { start: 0, end: 0 },
  dangerAreaPlus: { start: 0, end: 0 },
  stableArea: { start: 0, end: 0 },
};

export const getMaterialToAccountById = (account: Account, materialId: string): IMaterialToAccount => {
  return (
    account.materialsToAccount.find((item) => item.materialId === materialId) || {
      materialId: '',
      RiskTolerance: emptyRiskTolerance,
      RoutePriority: [],
      bufferStrategy: 0,
    }
  );
};
